import React from 'react';
import './Help.css';

const Help: React.FC = () => {
  return (
    <div className="help-guide">
      <h2 className="help-title">📚 Veiledning for Arrangører</h2>
      <p className="subtitle">
        En komplett guide til å arrangere suksessfulle Sykkelfest-arrangementer
      </p>

      {/* Introduction */}
      <div className="help-section">
        <h2><span className="icon">🎯</span> Hva er Sykkelfest?</h2>
        <p>
          Sykkelfest er en sosial sykkelarrangement der deltakerne sykler mellom private hjem og nyter 
          ulike måltider sammen. Konseptet går ut på at deltakere blir delt inn i par, og hvert par 
          besøker tre ulike hjem for forrett, hovedrett og dessert – før alle samles til en felles 
          avslutning på et forhåndsbestemt sted.
        </p>
        <div className="info-box success">
          <p>
            <strong>Målet:</strong> Skape sosiale møteplasser, og gi deltakerne 
            mulighet til å møte nye mennesker i sitt lokalområde.
          </p>
        </div>
      </div>

      {/* Getting Started */}
      <div className="help-section">
        <h2><span className="icon">🚀</span> Kom i gang</h2>
        
        <h3>Før du oppretter et arrangement</h3>
        <div className="checklist">
          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Bli arrangør</strong>
              <p>Søk om å bli arrangør via plattformen. Du vil motta en bekreftelse når søknaden er godkjent.</p>
            </div>
          </div>
          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Finn et egnet område</strong>
              <p>Velg et geografisk område der det er nok potensielle deltakere og varierte sykkelruter.</p>
            </div>
          </div>
          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Book sluttsted</strong>
              <p>Reserver et sted for den felles avslutningen (restaurant, park, kafé, etc.)</p>
            </div>
          </div>
          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Sett dato og tidspunkt</strong>
              <p>Velg en dato som gir deltakerne god tid til påmelding (minimum 2-3 uker frem i tid)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Creating Event */}
      <div className="help-section">
        <h2><span className="icon">📝</span> Opprette arrangement</h2>
        
        <h3>Trinn-for-trinn guide</h3>
        <p>Når du oppretter et arrangement, må du fylle ut følgende informasjon:</p>
        
        <ul>
          <li><strong>Arrangementsnavn:</strong> Gi arrangementet et beskrivende navn (f.eks. "Tour de Åndalsnes 2026")</li>
          <li><strong>By/område:</strong> Angi hvor arrangementet skal finne sted</li>
          <li><strong>Dato og tidspunkt:</strong> Når skal arrangementet starte?</li>
          <li><strong>Sluttdestinasjon:</strong> Adresse til der alle skal møtes til slutt (må være eksakt adresse)</li>
          <li><strong>Maksimal avstand:</strong> Hvor langt deltakerne maksimalt skal sykle - husk dette er radius fra sluttdestinasjonen (anbefalt: 5-10 km)</li>
          <li><strong>Beskrivelse:</strong> Fortell litt om arrangementet, hva deltakerne kan forvente</li>
          <li><strong>Antall deltakere:</strong> Minimum og maksimum antall par (må være delelig med 3, som er antall måltider)</li>
        </ul>

        <div className="tip-box">
          <strong>💡 Tips:</strong>
          <p>
            Start med et mindre arrangement første gang. Dette gjør det enklere å 
            håndtere logistikk og kommunikasjon.
          </p>
        </div>

        <h3>Hvilke måltider?</h3>
        <p>
          Du kan velge lengden for hvert måltid som skal serveres (forrett, hovedrett og dessert). Dette vil brukes for oppsett av tidsplanen. Hvert par vil bli tildelt å være vert for ett måltid.
        </p>

        <div className="info-box">
          <p>
            <strong>Viktig:</strong> Alle tre måltider (forrett, hovedrett, dessert) er obligatoriske 
            og kan ikke fjernes. Dette sikrer den tradisjonelle Sykkelfest-opplevelsen.
          </p>
        </div>
      </div>

      {/* Participant Recruitment */}
      <div className="help-section">
        <h2><span className="icon">👥</span> Få deltakere</h2>
        
        <h3>Markedsføring og rekruttering</h3>
        <p>For å få nok deltakere til arrangementet ditt, anbefaler vi:</p>
        
        <ul>
          <li><strong>Sosiale medier:</strong> Del arrangementet på Facebook, Instagram, lokale grupper</li>
          <li><strong>Invitasjonskode:</strong> Bruk den unike invitasjonskoden som genereres automatisk</li>
          <li><strong>Venner og nettverk:</strong> Start med å invitere folk du kjenner</li>
          <li><strong>Lokale grupper:</strong> Kontakt lag, organisasjoner, nabolag, idrettslag</li>
          <li><strong>Plakater:</strong> Heng opp plakater på kaféer, bibliotek, treningssentre</li>
        </ul>

        <div className="tip-box">
          <strong>💡 Tips:</strong>
          <p>
            Send ut påminnelser en uke før påmeldingsfristen. Mange venter til siste liten 
            med å melde seg på.
          </p>
        </div>

        <h3>Kommunikasjon med deltakere</h3>
        <p>
          Plattformen sender automatiske e-poster på vegne av deg, men det kan være lurt å:
        </p>
        <ul>
          <li>Opprette en Facebook-gruppe eller WhatsApp-gruppe for arrangementet</li>
          <li>Dele oppdateringer og bygge forventning</li>
          <li>Svare på spørsmål fra potensielle deltakere</li>
          <li>Du kan også sende meldinger til påmeldte deltagere gjennom Sykkelfest plattformen</li>
        </ul>
      </div>

      {/* Event Timeline */}
      <div className="help-section">
        <h2><span className="icon">📅</span> Arrangementsforløp</h2>
        
        <p>
          Her er en oversikt over hva som skjer automatisk i løpet av arrangementets levetid:
        </p>

        <div className="timeline">
          <div className="timeline-item">
            <strong>Ved opprettelse</strong>
            <p>Arrangementet opprettes med status "Påmelding åpen". Deltakere kan begynne å melde seg på.</p>
          </div>

          <div className="timeline-item">
            <strong>8 dager før arrangement (standard)</strong>
            <p>Du får varsel hvis det er for få påmeldte deltakere.</p>
          </div>

          <div className="timeline-item">
            <strong>6 dager før arrangement</strong>
            <p>Frist for deltakere til å avmelde seg. Etter dette kan de ikke lenger trekke seg.</p>
          </div>

          <div className="timeline-item">
            <strong>5 dager før arrangement kl 23:59</strong>
            <p>Påmeldingen stenger automatisk. Ingen flere påmeldinger mulig.</p>
          </div>

          <div className="timeline-item">
            <strong>4 dager før arrangement kl 05:00</strong>
            <p>Ruteplanen genereres automatisk av systemet.</p>
          </div>

          <div className="timeline-item">
            <strong>4 dager før arrangement kl 10:00</strong>
            <p>E-post sendes til alle som skal være verter med informasjon om hvilket måltid de har fått tildelt.</p>
          </div>

          <div className="timeline-item">
            <strong>2 dager før arrangement kl 18:00</strong>
            <p>Påminnelse sendes til alle deltakere.</p>
          </div>

          <div className="timeline-item">
            <strong>På arrangementsdagen</strong>
            <p>Progressive e-poster sendes ut med ruter til neste stopp etter hvert som arrangementet pågår. Meldingene er også tilgjengelig inne i Sykkelfest portalen for hver deltager.</p>
          </div>

          <div className="timeline-item">
            <strong>Etter siste stopp</strong>
            <p>Status endres til "Fullført". Statistikk genereres automatisk.</p>
          </div>

          <div className="timeline-item">
            <strong>1 dag etter arrangement kl 10:00</strong>
            <p>E-post sendes til alle deltakere med forespørsel om tilbakemelding.</p>
          </div>
        </div>

        <div className="info-box warning">
          <p>
            <strong>OBS:</strong> Tidspunktene over er standardinnstillinger og kan variere 
            basert på systeminnstillinger. Kontakt admin hvis du ønsker å endre tidsfrister.
          </p>
        </div>
      </div>

      {/* Managing Event */}
      <div className="help-section">
        <h2><span className="icon">⚙️</span> Administrere arrangement</h2>
        
        <h3>Følge med på påmeldinger</h3>
        <p>
          I arrangementsoversikten kan du se:
        </p>
        <ul>
          <li>Antall påmeldte par</li>
          <li>Om dere har nådd minimum antall deltakere</li>
          <li>Hvem som har meldt seg på (navn, e-post, telefon, adresse)</li>
          <li>Kostholdsrestriksjoner</li>
        </ul>

        <h3>Generere ruteplan</h3>
        <p>
          Ruteplanen genereres automatisk 4 dager før arrangementet. Systemet:
        </p>
        <ul>
          <li>Deler inn par i grupper på 3 (6 personer per måltid)</li>
          <li>Sørger for at ingen møter samme par mer enn én gang</li>
          <li>Optimaliserer sykkeldistanser</li>
          <li>Fordeler vertskap jevnt</li>
        </ul>

        <div className="info-box">
          <p>
            <strong>Manuell generering:</strong> Du kan også starte generering av ruteplanen manuelt 
            fra "Administrer arrangement"-siden ved å klikke "Generer ruter".
          </p>
        </div>

        <h3>Se statistikk</h3>
        <p>
          Etter at arrangementet er fullført, kan du se detaljert statistikk:
        </p>
        <ul>
          <li>Antall deltakere og par</li>
          <li>Total sykkeldistanse</li>
          <li>Antall måltider servert</li>
          <li>Kostholdsrestriksjoner</li>
          <li>Antall e-poster sendt</li>
        </ul>

        <div className="tip-box">
          <strong>💡 Tips:</strong>
          <p>
            Statistikken genereres automatisk når arrangementet fullføres, men du kan også 
            generere den manuelt ved å klikke "Generer statistikk" i administrasjonspanelet.
          </p>
        </div>
      </div>

      {/* During Event */}
      <div className="help-section">
        <h2><span className="icon">🚴</span> Under arrangementet</h2>
        
        <h3>Din rolle som arrangør</h3>
        <p>
          På selve arrangementsdagen er din hovedoppgave å:
        </p>
        <ul>
          <li>Være tilgjengelig for spørsmål og problemer</li>
          <li>Sørge for at sluttstedet er klart til å ta imot alle</li>
          <li>Skape god stemning når alle samles til slutt</li>
        </ul>

        <h3>Automatisk fremdrift</h3>
        <p>
          Systemet sender automatisk e-poster til deltakerne:
        </p>
        <ul>
          <li><strong>30 minutter før start:</strong> Rute til første stopp (forrett)</li>
          <li><strong>Midtveis i forretten:</strong> Rute til hovedrett</li>
          <li><strong>Midtveis i hovedretten:</strong> Rute til dessert</li>
          <li><strong>Midtveis i desserten:</strong> Rute til sluttdestinasjon</li>
        </ul>

        <div className="info-box success">
          <p>
            <strong>Automatikk:</strong> Du trenger ikke å gjøre noe for at meldingene skal sendes til deltagerne. 
            Systemet håndterer alt automatisk basert på tidsskjemaet. Deltagere må verifisere sin epost adresse i systemet for å få meldingene på epost.
          </p>
        </div>

        <h3>Håndtere problemer</h3>
        <p>Hvis noe går galt (sykdom, punktering, etc.):</p>
        <ul>
          <li>Ha telefonnummer til alle deltakere tilgjengelig</li>
          <li>Vær forberedt på å omdirigere folk hvis nødvendig</li>
          <li>Ha en backup-plan for akutte situasjoner</li>
        </ul>
      </div>

      {/* After Event */}
      <div className="help-section">
        <h2><span className="icon">🎉</span> Etter arrangementet</h2>
        
        <h3>Tilbakemeldinger</h3>
        <p>
          Dagen etter arrangementet får alle deltakere en e-post med forespørsel om tilbakemelding. 
          Du kan se tilbakemeldingene under "Tilbakemeldinger"-fanen i dashboardet.
        </p>

        <p>Deltakerne kan gi tilbakemelding på:</p>
        <ul>
          <li>Helhetsopplevelsen (1-5 stjerner)</li>
          <li>Plattformen (1-5 stjerner)</li>
          <li>Deg som arrangør (1-5 stjerner)</li>
          <li>Skrive en anbefaling som kan publiseres</li>
          <li>Dele oppskriften på måltidet de serverte</li>
        </ul>

        <h3>Lær og forbedre</h3>
        <p>
          Bruk tilbakemeldingene til å forbedre neste arrangement:
        </p>
        <ul>
          <li>Hva fungerte bra?</li>
          <li>Hva kan forbedres?</li>
          <li>Var avstanden passe?</li>
          <li>Var kommunikasjonen god nok?</li>
        </ul>

        <div className="tip-box">
          <strong>💡 Tips:</strong>
          <p>
            Send gjerne en personlig takk-e-post til alle deltakerne. Dette bygger 
            lojalitet og øker sjansen for at de melder seg på neste gang.
          </p>
        </div>
      </div>

      {/* Best Practices */}
      <div className="help-section">
        <h2><span className="icon">⭐</span> Beste praksis</h2>
        
        <h3>For et vellykket arrangement</h3>
        
        <div className="checklist">
          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Start markedsføring tidlig</strong>
              <p>Begynn å markedsføre arrangementet minst 3-4 uker før. Folk trenger tid til å planlegge.</p>
            </div>
          </div>

          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Kommuniser tydelig</strong>
              <p>Vær klar på hva deltakerne kan forvente: tidsramme, avstand, hva de må ta med.</p>
            </div>
          </div>

          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Tenk på tilgjengelighet</strong>
              <p>Velg et område med gode sykkelveier og variert terreng.</p>
            </div>
          </div>

          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Vær tilgjengelig</strong>
              <p>Gi ut ditt mobilnummer og vær klar til å hjelpe hvis noe går galt.</p>
            </div>
          </div>

          <div className="checklist-item">
            <div className="checkbox"></div>
            <div className="content">
              <strong>Skap fellesskap</strong>
              <p>Oppfordre til å dele bilder, lag en hashtag, skap gode minner sammen.</p>
            </div>
          </div>
        </div>

        <h3>Vanlige feil å unngå</h3>
        <ul>
          <li>❌ For kort tid mellom annonsering og arrangement</li>
          <li>❌ For stor maksimal sykkeldistanse (hold deg under 10 km)</li>
          <li>❌ Uklare beskrivelser av hva arrangementet innebærer</li>
          <li>❌ Ikke ha en backup-plan hvis noe går galt</li>
          <li>❌ Glemme å sjekke været og informere deltakerne</li>
        </ul>
      </div>

      {/* FAQ */}
      <div className="help-section">
        <h2><span className="icon">❓</span> Ofte stilte spørsmål</h2>
        
        <h3>Hva hvis vi ikke får nok påmeldte?</h3>
        <p>
          Du får automatisk varsel 8 dager før hvis antall påmeldte er lavt. Da kan du:
        </p>
        <ul>
          <li>Intensivere markedsføringen</li>
          <li>Kontakte admin for å utsette arrangementet</li>
          <li>Eventuelt avlyse og prøve igjen med mer markedsføring</li>
        </ul>

        <h3>Kan jeg endre arrangementet etter at folk har meldt seg på?</h3>
        <p>
          Mindre endringer (beskrivelse, tidspunkt) kan gjøres, men store endringer (dato, sted) 
          bør unngås. Kontakt admin hvis du må gjøre store endringer.
        </p>

        <h3>Hva hvis været blir dårlig?</h3>
        <p>
          Det er opp til deg som arrangør å bestemme om arrangementet skal gjennomføres ved dårlig vær. 
          Informer deltakerne i god tid hvis du vurderer å avlyse eller utsette.
        </p>

        <h3>Kan jeg se hvem som skal være verter før rutegenerering?</h3>
        <p>
          Nei, vertskapet fordeles automatisk når ruteplanen genereres. Dette sikrer rettferdig 
          fordeling og optimal rutelegging.
        </p>

        <h3>Hva hvis noen trekker seg rett før arrangementet?</h3>
        <p>
          Deltakere kan ikke avmelde seg etter 6 dager før arrangement. Hvis noen likevel må trekke 
          seg på grunn av sykdom eller akutt situasjon, kontakt admin for å håndtere dette manuelt.
        </p>

        <h3>Kan jeg ha flere arrangementer samtidig?</h3>
        <p>
          Ja, du kan ha flere aktive arrangementer. De vil alle vises i dashboardet ditt, 
          og du kan administrere dem separat.
        </p>
      </div>

      {/* Contact */}
      <div className="help-section">
        <h2><span className="icon">📞</span> Trenger du hjelp?</h2>
        
        <p>
          Hvis du har spørsmål som ikke er besvart i denne veiledningen, kan du:
        </p>
        
        <ul>
          <li>Kontakte support via e-post (se innstillinger for kontaktinformasjon)</li>
          <li>Kontakte andre arrangører i fellesskapet</li>
        </ul>

        <div className="info-box success">
          <p>
            <strong>Lykke til med arrangementet ditt! 🚴🎉</strong>
          </p>
          <p>
            Sykkelfest handler om å skape gode opplevelser og bygge fellesskap. Med god planlegging 
            og denne plattformen som verktøy, har du alt du trenger for å lykkes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Help;
